import React, {
  useMemo,
  useState,
  useEffect
} from 'react';
import PropTypes from 'prop-types';

import { AnimatePresence } from '@sopranodesign/framer-motion';
import {
  Flex,
  Table,
  Button,
  TableRow,
  Paragraph,
  TableBody,
  TableCell,
  TableHead,
  Pagination,
  PanelSlider,
  TableActions,
  LoadingContainer,
  OrderableTableRow,
  usePaginationReducer,
  useOrderableTableReducer
} from '@sopranodesign/pulse-components';
import { useQuery } from '@sopranodesign/pulse-hooks';
import {
  useAuth,
  PermissionFilter
} from '@sopranodesign/pulse-auth';

import { fetchZendeskTickets } from '../api';
import { StateLevel } from '.';

const ZendeskTicketsList = ({
  filterId,
  handleHide,
  onOpenTagsModal,
  onOpenRecrawlModal,
  loadingIds,
  errorIds
}) => {
  const { hasPermission } = useAuth();
  const canViewTag = hasPermission('view generative ai tag');
  const canEditTag = hasPermission('edit generative ai tag');
  const canEditResource = hasPermission('edit generative ai resource');

  const [shouldRefetch, setShouldRefetch] = useState(false);

  const order = useOrderableTableReducer({ id: `zendesk-tickets-${filterId}` });
  const pagination = usePaginationReducer({ id: `zendesk-tickets-${filterId}` });

  const { data, isLoading } = useQuery({
    queryKey: [
      'generative-ai/zendesk/tickets',
      filterId,
      { ...pagination.params, ...order.params }
    ],
    queryFn: () => fetchZendeskTickets(filterId, { ...pagination.params, ...order.params }),
    keepPreviousData: true,
    refetchInterval: shouldRefetch ? 5000 : false
  });

  const response = useMemo(() => data?.data ?? {}, [data]);
  const tickets = useMemo(() => response.data ?? [], [response]);
  const meta = response.meta ?? {};
  const filterName = response.filter_name ?? 'Tickets';

  const globalHasPending = response.has_pending ?? false;

  useEffect(() => {
    setShouldRefetch(globalHasPending);
  }, [globalHasPending]);

  return (
    <AnimatePresence>
      <PanelSlider header={filterName} handleHide={handleHide}>
        {isLoading && <LoadingContainer />}
        {!isLoading && !tickets.length && (
          <Paragraph mb secondary>No tickets available.</Paragraph>
        )}
        {!isLoading && tickets.length > 0 && (
          <Flex col>
            <Flex border xScroll>
              <Table>
                <TableHead>
                  <OrderableTableRow
                    rows={[
                      { label: 'Subject', sort: 'subject' },
                      { label: 'Status', sort: 'status' },
                      { label: 'Crawled At', sort: 'updated_at' },
                      ...(canViewTag ? [{ label: 'Tags' }] : []),
                      ...(((canViewTag && canEditTag) || canEditResource) ? [null] : [])
                    ]}
                    {...order.props}
                  />
                </TableHead>
                <TableBody>
                  {tickets.map(ticket => (
                    <TableRow key={ticket.id}>
                      <TableCell>{ticket.subject}</TableCell>
                      <TableCell>
                        <Flex center>
                          <StateLevel level={ticket.status} />
                          <span>{ticket.status}</span>
                        </Flex>
                      </TableCell>
                      <TableCell>{ticket.updated_at}</TableCell>

                      <PermissionFilter can="view generative ai tag">
                        <TableCell>
                          {ticket.groups && ticket.groups.length > 0
                            ? ticket.groups.map(group => group.name).join(', ')
                            : 'No Tags'}
                        </TableCell>
                      </PermissionFilter>

                      {((canViewTag && canEditTag) || canEditResource) && (
                        <TableActions>
                          <PermissionFilter can="view generative ai tag">
                            <PermissionFilter can="edit generative ai tag">
                              {ticket.status === 'Success' && (
                                <Button
                                  icon="tag"
                                  onClick={() => onOpenTagsModal(ticket)}
                                >
                                  Tags
                                </Button>
                              )}
                            </PermissionFilter>
                          </PermissionFilter>

                          <PermissionFilter can="edit generative ai resource">
                            <Button
                              icon="download"
                              loading={Boolean(loadingIds?.[ticket.id])}
                              disabled={globalHasPending}
                              onClick={() => onOpenRecrawlModal(ticket)}
                            >
                              Recrawl
                            </Button>
                          </PermissionFilter>

                          {errorIds?.[ticket.id] && (
                            <p style={{ color: 'red', marginTop: '4px' }}>
                              {errorIds[ticket.id]}
                            </p>
                          )}
                        </TableActions>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Flex>

            {typeof meta.current_page === 'number' && (
              <Pagination pagination={meta} {...pagination.props} />
            )}
          </Flex>
        )}
      </PanelSlider>
    </AnimatePresence>
  );
};

ZendeskTicketsList.propTypes = {
  filterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  handleHide: PropTypes.func.isRequired,
  onOpenTagsModal: PropTypes.func.isRequired,
  onOpenRecrawlModal: PropTypes.func.isRequired,
  loadingIds: PropTypes.object,
  errorIds: PropTypes.object.isRequired
};

export default ZendeskTicketsList;
